import { defineStore } from "pinia";
import { computed, ref } from "vue";
import * as graphql from "../graphql";
import { MaintenanceLockOp, type MaintenanceLockState, type Rsid } from "../graphql";
import apolloClient from "../graphql/client";
import { useTime } from "../util/time";
import { useClientStore } from "./client";
import { useServiceStore } from "./service";
import { useApolloErrorHandling } from "@/util/useApolloErrorHandling";

const lockTimeout = 600 * 1000; // 600 seconds = 10 minutes

export const useMaintenanceStore = defineStore("maintenance", () => {
  const client = useClientStore();
  const service = useServiceStore();
  const { handleApolloErrorDefault } = useApolloErrorHandling();
  const { humanDuration } = useTime();
  const subscription = {
    maintenanceLock: {
      unsubscribe() {},
    },
  };

  const humanLockTimeout = computed(() => humanDuration(lockTimeout));
  const consoleEnabled = ref<boolean>(false);
  const maintenanceLock = ref(<{ timeoutHandle?: number; expiresAt?: number }>{
    timeoutHandle: undefined,
    expiresAt: undefined,
  });
  const maintenanceLocked = computed(() => !!maintenanceLock.value?.timeoutHandle);
  // Maintenance lock only causes an interactivity lock for users without admin rights
  const maintenanceInteractivityLocked = computed(() => maintenanceLocked.value && !client.me?.jwt.hasAllServiceAccess);

  const durationToUnlock = computed(() =>
    maintenanceLock.value.expiresAt && client.serverTime
      ? maintenanceLock.value.expiresAt - client.serverTime
      : undefined,
  );
  const humanDurationToUnlock = computed(() =>
    durationToUnlock.value ? humanDuration(durationToUnlock.value) : undefined,
  );
  function setConsoleVisible(value: boolean) {
    consoleEnabled.value = value;
  }
  function validateMaintenanceLockState(maintenanceLockState: MaintenanceLockState) {
    console.debug("Committing maintenance lock state:", maintenanceLockState);
    switch (maintenanceLockState.op) {
      case MaintenanceLockOp.Lock:
        if (!maintenanceLockState.expiresAt) return;
        // Only replace the timeout if it would expire at a later date than the currently set one.
        // This is to facilitate different timeouts being pushed by different supporters, and not lowering the value
        // shown on screen or prematurely considering the lock lifted.
        if (!maintenanceLock.value.expiresAt || maintenanceLockState.expiresAt > maintenanceLock.value.expiresAt) {
          if (maintenanceLock.value.timeoutHandle !== undefined) {
            clearTimeout(maintenanceLock.value.timeoutHandle);
          }
          maintenanceLock.value.expiresAt = maintenanceLockState.expiresAt;
          if (!client.serverTime) {
            console.error("Server time not yet initialised but trying to activate maintenance lock");
            return;
          }
          const timeout = maintenanceLockState.expiresAt - client.serverTime;
          console.debug("Scheduling maintenance lock timeout for", timeout / 1000, "s from now");
          maintenanceLock.value.timeoutHandle = window.setTimeout(() => {
            maintenanceLock.value.timeoutHandle = undefined;
            maintenanceLock.value.expiresAt = undefined;
          }, timeout);
        }
        break;
      case MaintenanceLockOp.Unlock:
        // On unlock, everything is cleared unconditionally
        if (maintenanceLock.value.timeoutHandle !== undefined) {
          clearTimeout(maintenanceLock.value.timeoutHandle);
          maintenanceLock.value.timeoutHandle = undefined;
          maintenanceLock.value.expiresAt = undefined;
        }
        break;
    }
  }
  async function subscribeMaintenanceLockState(rsid: Rsid) {
    if (subscription.maintenanceLock) subscription.maintenanceLock.unsubscribe();
    const serviceId = rsid ?? service.ctx.service?.config?.rsid;
    if (!serviceId) return;
    const observable = apolloClient.subscribe({
      query: graphql.MaintenanceLockStateDocument,
      variables: {
        sid: serviceId.id,
        region: serviceId.region,
      },
    });
    subscription.maintenanceLock = observable.subscribe({
      next: (data) => {
        if (data.data?.maintenanceLockState) {
          validateMaintenanceLockState(data.data.maintenanceLockState);
        }
      },
    });
  }

  async function acquireMaintenanceLock(timeout: number) {
    const rsid = service.ctx.service?.config?.rsid;
    if (!rsid) return;
    try {
      await apolloClient.mutate({
        mutation: graphql.AcquireMaintenanceLockDocument,
        variables: {
          sid: rsid.id,
          region: rsid.region,
          timeout,
        },
      });
    } catch (e) {
      handleApolloErrorDefault(e);
    }
  }

  async function releaseMaintenanceLock() {
    const rsid = service.ctx.service?.config?.rsid;
    if (!rsid) return;
    try {
      await apolloClient.mutate({
        mutation: graphql.ReleaseMaintenanceLockDocument,
        variables: {
          sid: rsid.id,
          region: rsid.region,
        },
      });
    } catch (e) {
      handleApolloErrorDefault(e);
    }
  }
  return {
    subscribeMaintenanceLockState,
    acquireMaintenanceLock,
    releaseMaintenanceLock,
    durationToUnlock,
    maintenanceLocked,
    maintenanceInteractivityLocked,
    humanDurationToUnlock,
    lockTimeout,
    humanLockTimeout,
    setConsoleVisible,
    consoleEnabled,
  };
});
